import { DeltaStatic } from "quill";
import QuillImageDropAndPaste, { ImageData } from "quill-image-drop-and-paste";
import "quill-mention";
import "quill-mention/dist/quill.mention.css";
import {
  FunctionComponent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import ReactQuill, { Quill } from "react-quill";

/** custom history module */
const History = Quill.import("modules/history");
const getImgUrls = (delta: DeltaStatic) => {
  return (
    delta.ops
      ?.filter((i) => i.insert && i.insert.image)
      .map((i) => i.insert.image) ?? []
  );
};
class CustomHistory extends History {
  constructor(quill: Quill) {
    super(quill);
  }
  record(changeDelta: DeltaStatic, oldDelta: DeltaStatic) {
    const diff = this.quill.getContents().diff(oldDelta);
    const imgUrls = getImgUrls(diff);
    // do nothing when images are deleted
    if (imgUrls.length > 0) {
      return;
    }
    super.record(changeDelta, oldDelta);
  }
}

Quill.register("modules/history", CustomHistory);

/** custom embed */

var Embed = Quill.import("blots/embed");
class CopyBtn extends Embed {
  static create(html: string) {
    let node = super.create(html);
    node.classList.add("__copy__");
    node.setAttribute("data-html", html);
    node.innerHTML = "(Click to Copy)";
    return node;
  }
}

CopyBtn.blotName = "copy-btn";
CopyBtn.className = "copy-btn";
CopyBtn.tagName = "span";

Quill.register({
  "formats/copy-btn": CopyBtn,
});
Quill.register("modules/imageDropAndPaste", QuillImageDropAndPaste);

/** custom embed end */

type GearOption = {
  id: string;
  value: string;
};

interface RichEditorProps {
  searchGears: (term: string) => Promise<GearOption[]>;
  uploadImage: (file: File) => Promise<string>;
  deleteImages: (urls: string[]) => Promise<void>;
  value?: string;
  onChange?: (value: string) => void;
}

const colors = [
  "#000000",
  "#e60000",
  "#ff9900",
  "#ffff00",
  "#008a00",
  "#0066cc",
  "#9933ff",
  "#ffffff",
  "#facccc",
  "#ffebcc",
  "#ffffcc",
  "#cce8cc",
  "#cce0f5",
  "#ebd6ff",
  "#bbbbbb",
  "#f06666",
  "#ffc266",
  "#ffff66",
  "#66b966",
  "#66a3e0",
  "#c285ff",
  "#888888",
  "#a10000",
  "#b26b00",
  "#b2b200",
  "#006100",
  "#0047b2",
  "#6b24b2",
  "#444444",
  "#5c0000",
  "#663d00",
  "#666600",
  "#003700",
  "#002966",
  "#3d1466",
];

const formats = [
  "font",
  "header",
  "bold",
  "color",
  "background",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "copy",
  "copy-btn",
  "mention",
  "image",
  "video",
];

const RichEditor: FunctionComponent<RichEditorProps> = ({
  value,
  onChange,
  searchGears,
  uploadImage,
  deleteImages,
}) => {
  const ref = useRef<ReactQuill>(null);
  const embedImageUrl = useCallback((url: string) => {
    const quill = ref.current?.getEditor();
    if (!quill) return;
    let index = quill?.getSelection()?.index;
    if (index === undefined || index === null || index < 0) {
      index = ref.current?.getEditor().getLength() ?? 0;
    }
    quill.insertEmbed(index, "image", url, "user");
  }, []);
  const imageUploadHandler: QuillImageDropAndPaste["option"]["handler"] =
    useCallback(async (_, __, imageData?: ImageData) => {
      if (imageData) {
        const file = imageData.toFile();
        if (file) {
          // show loading
          const url = await uploadImage(file);
          if (url) {
            embedImageUrl(url);
          }
        }
      }
    }, []);

  useEffect(() => {
    if (ref.current) {
      const quill = ref.current.getEditor();
      const toolbar = quill.getModule("toolbar");
      toolbar.addHandler("copy", () => {
        const range = quill.getSelection();
        if (range?.length) {
          var rangeData = {
            start: range.index,
            end: range.index + range.length,
          };

          const contents = quill.getContents(rangeData.start, rangeData.end);
          const div = document.createElement("div");
          const tmpQuill = new Quill(div);
          tmpQuill.setContents(contents);
          const html = tmpQuill.root.innerHTML;

          quill.insertEmbed(range!.index + range!.length, "copy-btn", html);
        }
      });
      toolbar.addHandler("image", (clicked: boolean) => {
        if (clicked) {
          const input = document.createElement("input");
          input.setAttribute("type", "file");
          input.setAttribute("accept", "image/*");
          input.addEventListener("change", async (e) => {
            const file = (e.target as HTMLInputElement).files?.[0];
            if (file) {
              const url = await uploadImage(file);
              if (url) {
                embedImageUrl(url);
              }
            }
          });
          input.click();
        }
      });

      // we need to delete images when they are removed from the editor
      // also, we need to remove the history entry for the image deletion

      quill.on("text-change", (delta, oldContents, source) => {
        if (source !== "user") return;
        const deleted = getImgUrls(quill.getContents().diff(oldContents));
        deleteImages(deleted);
      });
    }
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, false] }],
          ["bold", "italic", "underline", "strike", "blockquote"],
          [
            { list: "ordered" },
            { list: "bullet" },
            { indent: "-1" },
            { indent: "+1" },
          ],
          [{ color: colors }, { background: colors }],
          ["link", "copy", "image", "video"],
          ["clean"],
        ],
        handlers: {
          copy: () => void 0,
          image: () => void 0,
        },
      },
      mention: {
        source: async (searchTerm: string, renderList: any) => {
          let result: GearOption[] = [];
          if (searchTerm.length > 0) {
            result = await searchGears(searchTerm);
          }
          renderList(result);
        },
      },
      imageDropAndPaste: {
        handler: imageUploadHandler,
      },
    }),
    []
  );
  useEffect(() => {
    const quill = ref.current?.getEditor();
    if (quill) {
      const currentHtmlValue = quill.root.innerHTML;
      if (currentHtmlValue !== value) {
        quill.root.innerHTML = value ?? "";
        // trigger change event
        quill.setContents(quill.getContents());
      }
    }
  }, [value]);
  return (
    <div>
      <style jsx="true">{`
        .ql-copy:after {
          content: "📋";
        }
      `}</style>
      <ReactQuill
        theme="snow"
        ref={ref}
        defaultValue={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
      />
    </div>
  );
};

export default RichEditor;
